import { useEffect, useMemo, useState } from "react";
import { api, campaignApi, type SpeciesApi, type TreeApi, type TreeUpdatePayload } from "@/lib/api";

type Props = {
  tree: TreeApi;
  onClose: () => void;
  onSaved: () => void;
};

// Correcao do cadastro do exemplar. So os campos alterados sao enviados e o
// motivo e obrigatorio: a correcao fica na linha do tempo (aba Historico).
export default function EditTreeModal({ tree, onClose, onSaved }: Props) {
  // Catalogo real (sem fallback de mock): o id escolhido vai para o banco.
  const [species, setSpecies] = useState<SpeciesApi[]>([]);
  useEffect(() => {
    api.species().then(setSpecies).catch(() => setSpecies([]));
  }, []);
  const [speciesId, setSpeciesId] = useState<string>(tree.species_id ? String(tree.species_id) : "");
  const [street, setStreet] = useState(tree.street ?? "");
  const [neighborhood, setNeighborhood] = useState(tree.neighborhood ?? "");
  const [reference, setReference] = useState(tree.address_reference ?? "");
  const [notes, setNotes] = useState("");
  const [lat, setLat] = useState(tree.latitude != null ? String(tree.latitude) : "");
  const [lng, setLng] = useState(tree.longitude != null ? String(tree.longitude) : "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const payload = useMemo<Partial<TreeUpdatePayload>>(() => {
    const body: Partial<TreeUpdatePayload> = {};
    const newSpecies = speciesId ? Number(speciesId) : null;
    if (newSpecies !== (tree.species_id ?? null)) body.species_id = newSpecies;
    const la = Number(lat.replace(",", "."));
    const lo = Number(lng.replace(",", "."));
    if (lat && lng && Number.isFinite(la) && Number.isFinite(lo) && (la !== tree.latitude || lo !== tree.longitude)) {
      body.latitude = la;
      body.longitude = lo;
      body.positioning_method = "MANUAL_COORDINATES";
    }
    if (street.trim() !== (tree.street ?? "")) body.street = street.trim();
    if (neighborhood.trim() !== (tree.neighborhood ?? "")) body.neighborhood = neighborhood.trim();
    if (reference.trim() !== (tree.address_reference ?? "")) body.address_reference = reference.trim();
    if (notes.trim()) body.field_notes = notes.trim();
    return body;
  }, [speciesId, lat, lng, street, neighborhood, reference, notes, tree]);

  const hasChanges = Object.keys(payload).length > 0;
  const canSave = hasChanges && reason.trim().length >= 3 && !saving;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await campaignApi.updateTree(tree.id, { ...payload, reason: reason.trim() });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full rounded-lg border border-default-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500";
  const label = "block text-xs font-semibold text-foreground-600 mb-1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-default-200 px-5 py-4">
          <div>
            <h2 className="text-base font-bold text-foreground-800">Editar cadastro</h2>
            <p className="text-xs text-foreground-500">{tree.cta_code}</p>
          </div>
          <button onClick={onClose} className="text-foreground-400 hover:text-foreground-700" aria-label="Fechar">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div>
            <label className={label}>Espécie</label>
            <select className={input} value={speciesId} onChange={(e) => setSpeciesId(e.target.value)}>
              <option value="">Não identificada</option>
              {species.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.popular_name} — {s.scientific_name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Latitude</label>
              <input className={input} value={lat} onChange={(e) => setLat(e.target.value)} inputMode="decimal" />
            </div>
            <div>
              <label className={label}>Longitude</label>
              <input className={input} value={lng} onChange={(e) => setLng(e.target.value)} inputMode="decimal" />
            </div>
          </div>

          <div>
            <label className={label}>Logradouro</label>
            <input className={input} value={street} onChange={(e) => setStreet(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Bairro</label>
              <input className={input} value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} />
            </div>
            <div>
              <label className={label}>Referência</label>
              <input className={input} value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
          </div>
          <div>
            <label className={label}>Observações (opcional)</label>
            <textarea className={input} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div>
            <label className={label}>
              Motivo da correção <span className="text-red-600">*</span>
            </label>
            <textarea
              className={input}
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: espécie conferida em vistoria; endereço corrigido"
            />
          </div>

          {!hasChanges && <p className="text-xs text-foreground-500">Altere pelo menos um campo para salvar.</p>}
          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-default-200 px-5 py-3">
          <button onClick={onClose} className="btn-secondary text-sm">
            Cancelar
          </button>
          <button onClick={save} disabled={!canSave} className="btn-primary text-sm disabled:opacity-50">
            {saving ? "Salvando..." : "Salvar correção"}
          </button>
        </div>
      </div>
    </div>
  );
}
